
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
} from "@mui/material";
import {
  Description,
  Receipt,
  Assessment,
  CalendarMonth,
  Task,
  CheckCircle,
  BarChart,
  LocalShipping,
} from "@mui/icons-material";
import Layout from "../components/layout/LandingPageLayout";
import { useTheme } from "@mui/material/styles";
import {Link} from "react-router-dom"

const GstInfo = () => {
  const theme = useTheme();

  const features = [
    {
      icon: <Description sx={{ fontSize: 40 }} />,
      title: "GSTR-1",
      subtitle: "Outward Supplies",
      description:
        "Streamline your outward supply returns with automated data compilation and validation.",
    },
    {
      icon: <Receipt sx={{ fontSize: 40 }} />,
      title: "GSTR-2",
      subtitle: "Inward Supplies",
      description:
        "Effortlessly manage inward supply returns with intelligent reconciliation features.",
    },
    {
      icon: <Assessment sx={{ fontSize: 40 }} />,
      title: "GSTR-3B",
      subtitle: "Monthly Return",
      description:
        "Simplify monthly return filing with real-time calculations and error detection.",
    },
    {
      icon: <CalendarMonth sx={{ fontSize: 40 }} />,
      title: "Annual Return",
      subtitle: "Filing",
      description:
        "Complete your annual return filing seamlessly with comprehensive data aggregation.",
    },
    {
      icon: <Task sx={{ fontSize: 40 }} />,
      title: "Task Management",
      subtitle: "Stay Organized",
      description:
        "Never miss a deadline with intelligent task scheduling and reminders.",
    },
    {
      icon: <CheckCircle sx={{ fontSize: 40 }} />,
      title: "Compliance",
      subtitle: "Stay Compliant",
      description:
        "Ensure 100% compliance with automated checks and regulatory updates.",
    },
    {
      icon: <BarChart sx={{ fontSize: 40 }} />,
      title: "Reporting",
      subtitle: "Insights",
      description:
        "Generate powerful reports and analytics for informed business decisions.",
    },
    {
      icon: <LocalShipping sx={{ fontSize: 40 }} />,
      title: "E-Way Bills",
      subtitle: "Transportation",
      description:
        "Generate and manage e-way bills instantly for hassle-free logistics.",
    },
  ];

  return (
    <Layout>
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}03 100%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background decorative elements */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.palette.primary.main}15 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.palette.primary.main}10 0%, transparent 70%)`,
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Header Section */}
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Chip
              label="FEATURES"
              sx={{
                mb: 2,
                bgcolor: theme.palette.primary.main,
                color: "white",
                fontWeight: 600,
                letterSpacing: 1.2,
                px: 2,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Comprehensive GST Solutions
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "text.secondary",
                maxWidth: 700,
                mx: "auto",
                lineHeight: 1.7,
                fontSize: { xs: "1rem", md: "1.1rem" },
              }}
            >
              Everything you need to manage your GST compliance efficiently.
              From filing returns to managing tasks, we've got you covered.
            </Typography>
          </Box>

          {/* Features Grid */}
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor: "divider",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 12px 24px ${theme.palette.primary.main}20`,
                      borderColor: theme.palette.primary.main,
                      "& .feature-icon": {
                        transform: "scale(1.1) rotate(5deg)",
                        color: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      height: "100%",
                    }}
                  >
                    <Box
                      className="feature-icon"
                      sx={{
                        color: theme.palette.primary.main,
                        mb: 2,
                        transition: "all 0.3s ease",
                        opacity: 0.9,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        color: "text.primary",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        mb: 1.5,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      {feature.subtitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.6,
                        fontSize: "0.875rem",
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Bottom CTA Section */}
          <Box
            sx={{
              mt: 8,
              p: 4,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                background:
                  "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.05) 10px, rgba(255,255,255,0.05) 20px)",
              }}
            />
            <Typography
              variant="h5"
              sx={{
                color: "white",
                fontWeight: 700,
                mb: 1,
                position: "relative",
                zIndex: 1,
              }}
            >
              Ready to streamline your GST compliance?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(255,255,255,0.9)",
                position: "relative",
                zIndex: 1,
              }}
            >
              Join thousands of businesses managing their GST efficiently with
              our platform
              <Box>
                 <Button
                component={Link}
                to="/login"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.primary.main,
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  "&:hover": {
                    bgcolor: theme.palette.grey[100],
                  },
                }}
              >
                Get Started Free
              </Button>
              </Box>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Layout>
  );
};

export default GstInfo;
